/**
 * Transcription fetching module
 * Handles fetching transcriptions from remote server via batch jobs
 */

import { showNotification } from '../components/notifications.js';
import { showConfirmModal } from '../components/modal.js';

// Track active batch polling intervals
const activeBatchPolls = new Map();

/**
 * Fetch transcription for a single episode
 */
export async function fetchTranscription(episodeId, hasExisting = false, buttonElement = null) {
    const button = buttonElement || event?.target;
    if (!button) return;

    // If it has existing transcription, ask for confirmation
    if (hasExisting && !button.dataset.confirmed) {
        const confirmed = await showConfirmModal({
            title: 'Re-fetch Transcription',
            message: 'This episode already has a transcription.\n\nDo you want to re-fetch it from the server?',
            confirmText: 'Re-fetch',
            cancelText: 'Cancel',
            icon: '📝'
        });
        if (confirmed) {
            button.dataset.confirmed = 'true';
        } else {
            return;
        }
    }

    const forceRefresh = hasExisting;

    button.disabled = true;
    button.textContent = 'Fetching...';

    try {
        const formData = new FormData();
        formData.append('force', forceRefresh);

        const response = await fetch(`/api/episodes/${episodeId}/fetch-transcription`, {
            method: 'POST',
            body: formData
        });
        const result = await response.json();

        if (response.ok) {
            button.textContent = '✅ Fetched!';
            showNotification(
                `Transcription fetched! ${result.transcription_length} chars, language: ${result.language}`,
                'success'
            );

            setTimeout(() => {
                if (window.loadDownloads) {
                    window.loadDownloads();
                }
            }, 1000);
        } else {
            button.textContent = '❌ Not Found';
            showNotification('Transcription not found on server', 'error');

            setTimeout(() => {
                button.textContent = '📝 Fetch Transcription';
                button.disabled = false;
            }, 2000);
        }
    } catch (error) {
        console.error('Error fetching transcription:', error);
        button.textContent = '❌ Error';
        setTimeout(() => {
            button.textContent = '📝 Fetch Transcription';
            button.disabled = false;
        }, 2000);
    }
}

/**
 * Fetch all transcriptions for a podcast using batch API
 */
export async function fetchAllTranscriptions(podcastId, podcastTitle, forceRefresh = false) {
    let message = 'This will check all downloaded episodes against the remote transcription server.';

    if (forceRefresh) {
        message += ' Force refresh mode: will re-fetch ALL transcriptions.';
    } else {
        message += ' Only episodes without transcriptions will be fetched.';
    }

    const confirmed = await showConfirmModal({
        title: 'Fetch Transcriptions',
        message: `${podcastTitle}\n\n${message}`,
        confirmText: forceRefresh ? 'Force Fetch All' : 'Fetch All',
        cancelText: 'Cancel',
        icon: '📝'
    });

    if (!confirmed) {
        return;
    }

    try {
        // Create batch job
        const response = await fetch(`/api/podcasts/${podcastId}/batch-transcribe${forceRefresh ? '?force=true' : ''}`, {
            method: 'POST'
        });

        const batchData = await response.json();

        if (!batchData.batch_id) {
            showNotification(batchData.message || 'No episodes need transcription', 'info');
            return;
        }

        showNotification(`Started transcription batch for ${batchData.total} episodes`, 'success');

        // Show progress modal and start polling
        showBatchProgressModal(batchData.batch_id, podcastTitle);

    } catch (error) {
        console.error('Error in fetchAllTranscriptions:', error);
        showNotification(`Error: ${error.message}`, 'error');
    }
}

/**
 * Show batch progress modal with polling
 */
function showBatchProgressModal(batchId, podcastTitle) {
    const modal = document.createElement('div');
    modal.className = 'modal';

    const content = document.createElement('div');
    content.className = 'modal-content';
    content.style.maxWidth = '600px';

    const title = document.createElement('h2');
    title.textContent = `📝 Fetching Transcriptions - ${podcastTitle}`;
    content.appendChild(title);

    // Progress container
    const progressContainer = document.createElement('div');
    progressContainer.className = 'progress-container';

    const progressBar = document.createElement('div');
    progressBar.className = 'progress-bar';
    progressBar.style.width = '0%';

    const progressText = document.createElement('div');
    progressText.className = 'progress-text';
    progressText.textContent = '0%';

    progressContainer.appendChild(progressBar);
    progressContainer.appendChild(progressText);
    content.appendChild(progressContainer);

    // Stats grid
    const statsGrid = document.createElement('div');
    statsGrid.style.cssText = 'display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin: 1.5rem 0;';

    const statLabels = ['Total', 'Pending', 'Completed', 'Failed'];
    const statElements = {};

    statLabels.forEach(label => {
        const card = document.createElement('div');
        card.className = 'stat-card';

        const value = document.createElement('div');
        value.className = 'stat-value';
        value.textContent = '0';
        statElements[label.toLowerCase()] = value;

        const labelEl = document.createElement('div');
        labelEl.className = 'stat-label';
        labelEl.textContent = label;

        card.appendChild(value);
        card.appendChild(labelEl);
        statsGrid.appendChild(card);
    });

    content.appendChild(statsGrid);

    // Status text
    const statusText = document.createElement('div');
    statusText.style.cssText = 'text-align: center; color: var(--text-secondary); margin-bottom: 1rem;';
    statusText.textContent = 'Processing in background...';
    content.appendChild(statusText);

    // Button container
    const btnContainer = document.createElement('div');
    btnContainer.style.cssText = 'display: flex; justify-content: flex-end;';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn btn-secondary';
    cancelBtn.textContent = 'Cancel';
    btnContainer.appendChild(cancelBtn);
    content.appendChild(btnContainer);

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Poll for batch status
    const pollInterval = setInterval(async () => {
        try {
            const response = await fetch(`/api/batches/${batchId}`);
            const status = await response.json();

            statElements.total.textContent = status.total;
            statElements.pending.textContent = status.pending + (status.submitted || 0);
            statElements.completed.textContent = status.completed;
            statElements.failed.textContent = status.failed;

            const processed = status.completed + status.failed;
            const percentage = status.total > 0 ? Math.round((processed / status.total) * 100) : 0;
            progressBar.style.width = percentage + '%';
            progressText.textContent = percentage + '%';

            if (status.status === 'completed' || status.status === 'cancelled') {
                clearInterval(pollInterval);
                activeBatchPolls.delete(batchId);

                progressBar.style.width = '100%';
                progressText.textContent = '100%';

                statusText.style.color = 'var(--success)';
                statusText.textContent = `✓ ${status.status === 'cancelled' ? 'Cancelled' : 'Complete'}! ${status.completed} successful, ${status.failed} failed`;

                cancelBtn.textContent = 'Close';
                cancelBtn.onclick = () => {
                    modal.remove();
                    if (window.loadPodcasts) {
                        window.loadPodcasts();
                    }
                };

                showNotification(
                    `Transcription batch ${status.status}! ${status.completed} successful, ${status.failed} failed`,
                    status.status === 'completed' ? 'success' : 'info'
                );
            } else {
                statusText.textContent = `Processing... ${processed}/${status.total} items`;
            }

        } catch (error) {
            console.error('Error polling batch status:', error);
        }
    }, 3000);

    activeBatchPolls.set(batchId, pollInterval);

    cancelBtn.onclick = async () => {
        clearInterval(pollInterval);
        activeBatchPolls.delete(batchId);

        try {
            await fetch(`/api/batches/${batchId}`, { method: 'DELETE' });
            showNotification('Batch cancelled', 'info');
        } catch (error) {
            console.error('Error cancelling batch:', error);
        }

        modal.remove();
    };
}
